import React, { useState, useEffect } from 'react';
import { getWallet, getWalletTransactions, createStripeSession, getStripeStatus } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownLeft, CreditCard, Banknote, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';

const Wallet = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  const fetchData = async () => {
    try {
      const [walletRes, transactionsRes] = await Promise.all([
        getWallet(),
        getWalletTransactions()
      ]);
      setWallet(walletRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
      toast.error('Gabim në ngarkimin e portofolit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Kontrollo për përfundimin e pagesës Stripe
  useEffect(() => {
    const checkPayment = async () => {
      if (sessionId) {
        try {
          const response = await getStripeStatus(sessionId);
          if (response.data.payment_status === 'paid') {
            toast.success('Pagesa u krye me sukses!', {
              description: `Portofoli u mbush me ${formatCurrency(response.data.amount)}`
            });
            fetchData();
          }
        } catch (error) {
          console.error('Failed to check payment status:', error);
        }
      }
    };
    checkPayment();
  }, [sessionId]);

  const handleTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error('Vendosni një shumë të vlefshme');
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await createStripeSession(parseFloat(topupAmount));
      // Ridrejto te Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create payment session:', error);
      toast.error('Gabim në krijimin e sesionit të pagesës');
      setProcessingPayment(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'online_topup':
        return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'cash_topup':
        return <Banknote className="w-4 h-4 text-green-500" />;
      case 'charging_debit':
        return <Zap className="w-4 h-4 text-red-500" />;
      default:
        return <ArrowUpRight className="w-4 h-4 text-zinc-500" />;
    }
  };

  const getTransactionColor = (amount) => {
    return amount >= 0 ? 'text-green-500' : 'text-red-500';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-48" />
        <div className="skeleton h-48 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-outfit">Portofoli Im</h1>
          <p className="text-zinc-400 mt-1">Menaxhoni bilancin dhe transaksionet</p>
        </div>
        <Button onClick={() => setTopupDialogOpen(true)} data-testid="topup-btn">
          <Plus className="w-4 h-4 mr-2" />
          Mbush Portofolin
        </Button>
      </div>

      {/* Karta e bilancit */}
      <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-400">Bilanci aktual</p>
              <p className="text-4xl font-bold text-white font-outfit mt-2">
                {formatCurrency(wallet?.balance || 0)}
              </p>
              <p className="text-sm text-zinc-500 mt-2">
                1 kredit = 1 LEK
              </p>
            </div>
            <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center">
              <WalletIcon className="w-10 h-10 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Opsionet e shpejta të mbushjes */}
      <div className="grid grid-cols-4 gap-4">
        {[500, 1000, 2000, 5000].map((amount) => (
          <Button
            key={amount}
            variant="outline"
            className="h-16"
            onClick={() => {
              setTopupAmount(amount.toString());
              setTopupDialogOpen(true);
            }}
            data-testid={`quick-topup-${amount}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            {formatCurrency(amount)}
          </Button>
        ))}
      </div>

      {/* Transaksionet */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-outfit">Transaksionet</CardTitle>
          <CardDescription>Historia e transaksioneve të portofolit</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-zinc-400">Lloji</TableHead>
                <TableHead className="text-zinc-400">Përshkrimi</TableHead>
                <TableHead className="text-zinc-400">Shuma</TableHead>
                <TableHead className="text-zinc-400">Bilanci pas</TableHead>
                <TableHead className="text-zinc-400">Data</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-zinc-500 py-12">
                    <WalletIcon className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                    <p>Nuk ka transaksione</p>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => (
                  <TableRow key={tx.id} className="border-white/5 hover:bg-white/5">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(tx.type)}
                        <span className="text-zinc-300 capitalize">
                          {tx.type.replace('_', ' ')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400">{tx.description}</TableCell>
                    <TableCell className={getTransactionColor(tx.amount)}>
                      {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell className="text-zinc-300">
                      {formatCurrency(tx.balance_after)}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {formatDate(tx.created_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialogu i mbushjes */}
      <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">Mbush Portofolin</DialogTitle>
            <DialogDescription>
              Mbushni portofolin tuaj me Stripe
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="topup_amount">Shuma (LEK)</Label>
              <div className="relative">
                <Input
                  id="topup_amount"
                  type="number"
                  min="100"
                  step="100"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  placeholder="1000"
                  className="pr-12 text-lg"
                  data-testid="topup-amount-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500">
                  LEK
                </span>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {[500, 1000, 2000, 5000].map((amount) => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setTopupAmount(amount.toString())}
                  className={topupAmount === amount.toString() ? 'border-primary' : ''}
                >
                  {amount}
                </Button>
              ))}
            </div>

            <div className="p-4 rounded-lg bg-zinc-800/50">
              <p className="text-sm text-zinc-400">Do të paguani:</p>
              <p className="text-2xl font-bold text-white mt-1">
                {formatCurrency(parseFloat(topupAmount) || 0)}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTopupDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              onClick={handleTopup} 
              disabled={processingPayment || !topupAmount}
              data-testid="confirm-topup-btn"
            >
              {processingPayment ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Duke procesuar...
                </div>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Paguaj me Stripe
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wallet;
